import Button from 'antd/lib/button';
import Card from 'antd/lib/card/Card';
import Col from 'antd/lib/col';
import Divider from 'antd/lib/divider';
import Empty from 'antd/lib/empty';
import Row from 'antd/lib/row';
import Tag from 'antd/lib/tag';
import Typography from 'antd/lib/typography';
import { sortBy } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { currencyUSDFormatter } from '../../domain/common/currencyFormatters';
import { formatToMinutesAndHours } from '../../domain/common/dateFormatters';
import { IPriceResponseTrigger, PriceType } from '../../domain/price-response/interface';
import { useCompanies } from '../selectors/GlobalCompanySelector/CompanyContext';
import { ReactComponent as TargetIcon } from '../icons/target.svg';
import { TriggerTimeTag } from '../TriggerTimeTag';

interface ITriggerPriceWidget {
    triggers: IPriceResponseTrigger[] | null | undefined;
    loading?: boolean;
}

const PRICE_TYPE_LABEL: Record<string, string> = {
    [PriceType.REAL_TIME]: 'Real Time',
    [PriceType.DAY_AHEAD]: 'Day Ahead',
};

const PRICE_TYPE_COLOR: Record<string, string> = {
    [PriceType.REAL_TIME]: 'blue',
    [PriceType.DAY_AHEAD]: 'purple',
};

interface ITriggerPriceCard {
    trigger: IPriceResponseTrigger;
}

const PriceTriggerCard = ({ trigger }: ITriggerPriceCard) => {
    return (
        <Card size="small" style={{ gap: '10px' }}>
            <Row align="middle" gutter={[0, 0]} style={{ margin: '15px 0' }}>
                {/* Price + Price Type */}
                <Col xs={24} md={7}>
                    <Row justify="center" align="middle" gutter={8} wrap={false} style={{ height: '48px' }}>
                        <Col>
                            <Typography.Title level={3} className="trigger-price" style={{ margin: 0 }}>
                                {currencyUSDFormatter.format(trigger.price)}
                            </Typography.Title>

                            <Typography.Text type="secondary">Per MWh</Typography.Text>
                        </Col>
                    </Row>
                </Col>

                <Col xs={0} md={2}>
                    <Divider type="vertical" style={{ height: '48px', margin: 0 }} />
                </Col>
                <Col xs={24} md={0}>
                    <Divider style={{ height: '100%' }} />
                </Col>

                {/* Trigger details */}
                <Col xs={24} md={15}>
                    <Row justify="space-around" align="middle">
                        <Col style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Typography.Text type="secondary" className="trigger-detail-label">
                                Market
                            </Typography.Text>
                            <Tag color={PRICE_TYPE_COLOR[trigger.price_type] || 'default'} style={{ margin: 0 }}>
                                {PRICE_TYPE_LABEL[trigger.price_type] || trigger.price_type}
                            </Tag>
                        </Col>

                        <Col style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Typography.Text type="secondary" className="trigger-detail-label">
                                Trigger Time
                            </Typography.Text>
                            <div className="trigger-detail-value">
                                <TriggerTimeTag value={trigger} />
                            </div>
                        </Col>

                        <Col style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Typography.Text type="secondary" className="trigger-detail-label">
                                Duration
                            </Typography.Text>
                            <div className="trigger-detail-value">{formatToMinutesAndHours(trigger.duration)}</div>
                        </Col>

                        <Col style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Typography.Text type="secondary" className="trigger-detail-label">
                                Stop at Lower
                            </Typography.Text>

                            <Tag color={trigger.cancel_when_price_lower ? 'green' : 'default'} style={{ margin: 0 }}>
                                {trigger.cancel_when_price_lower ? 'Enabled' : 'Disabled'}
                            </Tag>
                        </Col>
                    </Row>
                </Col>
            </Row>
        </Card>
    );
};

export const PriceTriggersWidget = ({ triggers, loading }: ITriggerPriceWidget) => {
    const { companyId } = useCompanies()!;
    const navigation = useNavigate();

    const activeTriggers = sortBy(
        triggers?.filter(trigger => !!trigger && !!trigger.company_id),
        'price'
    );

    const hasTriggers = !!activeTriggers && activeTriggers.length > 0;

    const navigateToPriceResponsePage = () => {
        const companyQuery = companyId ? `companyId=${companyId}` : '';
        navigation(`/price-response/triggers?sorter.field=date_modified&sorter.order=descend&${companyQuery}`);
    };

    return (
        <Card
            title={
                <Row justify="space-between" align="middle">
                    <Col style={{ marginLeft: '50px' }}>
                        <Row justify="center" align="middle" gutter={8}>
                            <Col>
                                <TargetIcon className="target-icon" />
                            </Col>
                            <Col>
                                <Typography.Title level={4} style={{ color: '#fff', margin: 0 }}>
                                    Active Price Triggers
                                </Typography.Title>
                            </Col>
                        </Row>
                    </Col>
                    <Col>
                        <Button type="primary" onClick={navigateToPriceResponsePage}>
                            See all Price Triggers
                        </Button>
                    </Col>
                </Row>
            }
            styles={{ header: { background: '#2fadab' } }}
            className="trigger-price-card"
            loading={loading}
        >
            <div>
                {hasTriggers ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                        {activeTriggers.map(trigger => (
                            <PriceTriggerCard key={trigger.id} trigger={trigger} />
                        ))}
                    </div>
                ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Currently, you have no triggers" />
                )}
            </div>
        </Card>
    );
};
